
// src/store/slices/productsSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { fetchImagesFromStorage } from '../../services/storageService';

// Product categories
const PRODUCT_CATEGORIES = {
  FLOORS: 'Floors',
  WALLS: 'Walls'
};

// Curated featured images - these will be prioritized on the home page
const FEATURED_IMAGES = {
  floors: ['floor17', 'floor24', 'floor32'],
  walls: ['wall19', 'wall52', 'wall11']
};

// Sample product data (maintaining name, price, description)
const graniteProducts = [
  {
    id: 'product1',
    name: 'Black Galaxy Granite',
    price: 1200,
    description: 'Premium black granite with gold flecks, perfect for kitchen countertops.',
    category: PRODUCT_CATEGORIES.FLOORS
  },
  {
    id: 'product2',
    name: 'Kashmir White Granite',
    price: 1400,
    description: 'Elegant white granite with subtle patterns, ideal for modern interiors.',
    category: PRODUCT_CATEGORIES.FLOORS
  },
  {
    id: 'product3',
    name: 'Blue Pearl Granite',
    price: 1600,
    description: 'Distinctive blue-gray granite with iridescent reflections.',
    category: PRODUCT_CATEGORIES.WALLS
  },
  {
    id: 'product4',
    name: 'Tan Brown Granite',
    price: 1300,
    description: 'Rich brown granite with black and burgundy mineral deposits.',
    category: PRODUCT_CATEGORIES.WALLS
  }
];

// Async thunk for fetching products and their images
export const fetchProducts = createAsyncThunk(
  'products/fetchProducts',
  async (_, { rejectWithValue }) => {
    try {
      // Fetch floor images from Firebase Storage
      const floorImagesData = await fetchImagesFromStorage('floors');
      
      // Fetch wall images from Firebase Storage
      const wallImagesData = await fetchImagesFromStorage('walls');
      
      // Function to prioritize specific featured images
      const prioritizeImages = (images, featuredNames) => {
        // Filter out the featured images first
        const featured = images.filter(img => 
          featuredNames.some(name => img.name.includes(name))
        );
        
        // Get remaining images
        const remaining = images.filter(img => 
          !featuredNames.some(name => img.name.includes(name))
        );
        
        // Sort featured images to match the order in featuredNames
        featured.sort((a, b) => {
          const aIndex = featuredNames.findIndex(name => a.name.includes(name));
          const bIndex = featuredNames.findIndex(name => b.name.includes(name));
          return aIndex - bIndex;
        });
        
        // Return featured images first, then the rest
        return [...featured, ...remaining];
      };
      
      // Prioritize the specified floor images
      const sortedFloorImages = prioritizeImages(floorImagesData, FEATURED_IMAGES.floors);
      
      // Prioritize the specified wall images
      const sortedWallImages = prioritizeImages(wallImagesData, FEATURED_IMAGES.walls);
      
      // Create floor products with images
      const floorProducts = sortedFloorImages.map((imageData, index) => {
        // Extract number from filename for more descriptive naming
        const numberMatch = imageData.name.match(/\d+/);
        const number = numberMatch ? numberMatch[0] : '';
        
        // Get the matching product data if available, or create a default one
        const baseProduct = graniteProducts.find(p => p.category === PRODUCT_CATEGORIES.FLOORS && p.id === `product${index + 1}`) || {
          id: `floor${number || index + 1}`,
          name: `Premium Floor Granite ${number || index + 1}`,
          price: 1200 + (index * 100),
          description: 'High-quality granite flooring for elegant interiors.',
          category: PRODUCT_CATEGORIES.FLOORS
        };
        
        return {
          ...baseProduct,
          image: imageData.url,
          imageName: imageData.name,
          id: baseProduct.id || `floor${number || index + 1}`
        };
      });
      
      // Create wall products with images
      const wallProducts = sortedWallImages.map((imageData, index) => {
        // Extract number from filename for more descriptive naming
        const numberMatch = imageData.name.match(/\d+/);
        const number = numberMatch ? numberMatch[0] : '';
        
        // Get the matching product data if available, or create a default one
        const baseProduct = graniteProducts.find(p => p.category === PRODUCT_CATEGORIES.WALLS && p.id === `product${index + 3}`) || {
          id: `wall${number || index + 1}`,
          name: `Premium Wall Granite ${number || index + 1}`,
          price: 1600 + (index * 100),
          description: 'Elegant granite for wall cladding and decorative surfaces.',
          category: PRODUCT_CATEGORIES.WALLS
        };
        
        return {
          ...baseProduct,
          image: imageData.url,
          imageName: imageData.name,
          id: baseProduct.id || `wall${number || index + 1}`
        };
      });
      
      // Combine floor and wall products
      const allProducts = [...floorProducts, ...wallProducts];
      
      return allProducts;
    } catch (error) {
      console.error('Error in fetchProducts:', error);
      return rejectWithValue(error.message);
    }
  }
);

const productsSlice = createSlice({
  name: 'products',
  initialState: {
    items: [],
    status: 'idle', // 'idle' | 'loading' | 'succeeded' | 'failed'
    error: null
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchProducts.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchProducts.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.items = action.payload;
      })
      .addCase(fetchProducts.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      });
  },
});

export default productsSlice.reducer;